<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import { useIdeStore } from '@/stores/ide.store'

import closeTag from '@/assets/images/ide/Close.svg'
import tick from '@/assets/images/teach-and-assess/tick.svg'

import { useOrganisationStore } from '@/stores/organisation.store'

import projectsService, {
  type IInviteCollaborationUsers,
  type IHttpStatus,
  GENERIC_TYPE
} from '@/services/ide/projects.service'
import { getStudentGroups } from '@/services/teach.service'

import Button from '@/components/atoms/button/index.vue'
import type { IGenericLoading } from '@/components/ide/ide/settings/ManageCollaboratorModal.vue'

const emit = defineEmits(['handleOpenShareProject'])

const props = defineProps({
  view: {
    type: String,
    required: true
  }
})

interface User {
  email: string | null
  firstName: string | null
  lastName: string | null
  name?: string | null
  insStudentGroupId?: number | null
}

enum COLLAB_TYPE {
  INVITE_USER = 'Invite user',
  INVITE_GROUP = 'Invite groups'
}

const ideStore = useIdeStore()
const organisationStore = useOrganisationStore()

const searchCollaborator = ref<string>('')
const userList = ref<User[]>([])
const httpStatus = ref<IHttpStatus>({ status: '', message: '' })
const httpSuccessTimeOut = ref<NodeJS.Timeout | number | null>(null)
const isGenericLoading = ref<IGenericLoading>({ type: '', message: '' })
const collabTabStatus = ref<COLLAB_TYPE>(COLLAB_TYPE.INVITE_USER)
const tags = ref<string[]>([])
const tagsAPI = ref<string[]>([])
const readOnlyPermission = ref<boolean>(false)
const permissionDropdownContainer = ref<HTMLElement | null>(null)
const permissionDropdown = ref<boolean>(false)
const showDropdown = ref<boolean>(false)
const dropdownContainer = ref<HTMLElement | null>(null)
const dropdownOffset = ref<number>(0)
const forkProjectCheck = ref<boolean>(false)

const currentOrgCode = computed(() => organisationStore.currentOrgCode!)

const projectDetails = computed(() => {
  return ideStore.isSelectedProject ? ideStore.isSelectedProject : ideStore.isProject
})

const searchUserList = computed(() => {
  if (searchCollaborator.value !== '') {
    return userList.value.filter((list: User) => {
      return searchCollaborator.value
        .split(' ')
        .every(
          (users) =>
            (list?.firstName ?? '').toLowerCase().includes(users.toLowerCase()) ||
            (list?.lastName ?? '').toLowerCase().includes(users.toLowerCase()) ||
            (list?.name ?? '').toLowerCase().includes(users.toLowerCase())
        )
    })
  } else return userList.value
})

/**
 * @description - gets all the users for the respective organization
 */
const getAllUsers = async () => {
  userList.value = []
  isGenericLoading.value = { type: GENERIC_TYPE.FETCH, message: 'Loading...' }
  await projectsService
    .listOfUsers(projectDetails.value)
    .then((response: any) => {
      userList.value = response.data
      isGenericLoading.value = { type: '', message: '' }
    })
    .catch(() => (isGenericLoading.value = { type: '', message: '' }))
}
/**
 * @description - gets all the group for the respective organization
 */
const getAllGroupUsers = async () => {
  userList.value = []
  isGenericLoading.value = { type: GENERIC_TYPE.FETCH, message: 'Loading...' }
  await getStudentGroups(currentOrgCode.value)
    .then((response: any) => {
      userList.value = response
      isGenericLoading.value = { type: '', message: '' }
    })
    .catch(() => (isGenericLoading.value = { type: '', message: '' }))
}

/**
 * @description - to add collaborator
 */
const addCollaborator = async () => {
  const addCollaboratorReqObject: IInviteCollaborationUsers = {
    projectId: projectDetails.value.id,
    readOnly: readOnlyPermission.value,
    forked: forkProjectCheck.value,
    invitedUsers: collabTabStatus.value == COLLAB_TYPE.INVITE_USER ? tagsAPI.value : [],
    studentGroupIds: collabTabStatus.value == COLLAB_TYPE.INVITE_GROUP ? tagsAPI.value : []
  }
  isGenericLoading.value = { type: GENERIC_TYPE.ADD, message: 'Add Collaborator...' }
  httpStatus.value = { status: GENERIC_TYPE.LOADING, message: 'Loading...' }
  await projectsService
    .addCollaboratorAndGroupsInProject(addCollaboratorReqObject)
    .then(() => {
      httpStatus.value = {
        status: GENERIC_TYPE.SUCCESS,
        message: 'User added to the project successfully'
      }
      isGenericLoading.value = { type: '', message: '' }
      tags.value = []
      tagsAPI.value = []
      emit('handleOpenShareProject')
    })
    .catch((error) => {
      if (error?.response?.status === 429) {
        httpStatus.value = {
          status: GENERIC_TYPE.ERROR,
          message: 'Limit reached. Please upgrade your plan.'
        }
      } else {
        httpStatus.value = {
          status: GENERIC_TYPE.ERROR,
          message: 'Adding collaboration failed'
        }
      }
      isGenericLoading.value = { type: '', message: '' }
    })
}

/**
 * @param value - taking the value of collab type
 * @description changing the collab type accordingly
 */
const handleCollabTabStatus = (value: COLLAB_TYPE) => {
  collabTabStatus.value = value
}

/**
 * @description - Function to add a tag from the dropdown
 * @param user - adding user details
 */
const addTag = (user: User) => {
  if (
    (!tags.value.includes(user.name as string) &&
      collabTabStatus.value === COLLAB_TYPE.INVITE_GROUP) ||
    (!tagsAPI.value.includes(user.email as string) &&
      collabTabStatus.value === COLLAB_TYPE.INVITE_USER)
  ) {
    if (
      (collabTabStatus.value === COLLAB_TYPE.INVITE_USER && tags.value.length < 5) ||
      collabTabStatus.value === COLLAB_TYPE.INVITE_GROUP
    ) {
      tags.value.push((user.firstName as string) ?? user.name)
      tagsAPI.value.push((user.email as string) ?? user.insStudentGroupId)
    } else {
      httpStatus.value = {
        status: GENERIC_TYPE.ERROR,
        message: 'Cannot invite more than 5 user'
      }
    }
  }
  searchCollaborator.value = ''
}
/**
 * @param index - taking the tag to remove
 * @description Function to remove tag
 */
const removeTag = (index: number) => {
  tags.value.splice(index, 1)
  tagsAPI.value.splice(index, 1)
}

/**
 * @param value - changing the permission
 * @description - to change the permission
 */
const changePermissionStatus = (value: boolean) => {
  readOnlyPermission.value = value
  permissionDropdown.value = false
}

/**
 * @description - Function to toggle dropdown visibility
 */
const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value
  if (showDropdown.value) {
    adjustDropdownPosition()
  }
}

/**
 * Function to adjust the dropdown position based on the height of the parent div
 */
const adjustDropdownPosition = () => {
  const parentHeight = dropdownContainer.value?.offsetHeight || 0
  dropdownOffset.value = parentHeight
}

/**
 * @description - Close the dropdown if clicked outside
 * @param event - any
 */
const handleClickOutside = (event: any) => {
  if (dropdownContainer.value && !dropdownContainer.value.contains(event.target)) {
    showDropdown.value = false
  }
  if (
    permissionDropdownContainer.value &&
    !permissionDropdownContainer.value.contains(event.target)
  ) {
    permissionDropdown.value = false
  }
}

onMounted(() => {
  getAllUsers()
  document.addEventListener('click', handleClickOutside)
  adjustDropdownPosition()
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

watch(httpStatus, () => {
  if (httpStatus.value) {
    if (httpSuccessTimeOut.value) clearTimeout(httpSuccessTimeOut.value)
    httpSuccessTimeOut.value = setTimeout(() => {
      httpStatus.value = { status: '', message: '' }
    }, 3000)
  }
})

watch(
  () => [collabTabStatus.value, props.view],
  () => {
    if (collabTabStatus.value === COLLAB_TYPE.INVITE_USER) getAllUsers()
    else getAllGroupUsers()
    tags.value = []
    tagsAPI.value = []
    searchCollaborator.value = ''
    forkProjectCheck.value = false
    readOnlyPermission.value = false
  }
)

watch(
  () => searchCollaborator.value,
  () => {
    searchUserList
  }
)
</script>

<template>
  <div class="relative flex flex-col items-center px-2 pb-3">
    <div class="flex w-full flex-row-reverse items-center justify-between px-2 pb-6 sm:flex-row">
      <span class="text-mid pl-3 text-base font-medium sm:pl-0"
        >Manage the users that can access your coding project</span
      >
      <button
        class="flex w-fit items-center rounded-full border border-new-secondary-600 p-3"
        @click="emit('handleOpenShareProject')"
      >
        <FontAwesomeIcon icon="fa-chevron-left" class="h-2 w-2" />
      </button>
    </div>
    <div class="flex w-full justify-center gap-5 font-bold">
      <div
        @click="handleCollabTabStatus(COLLAB_TYPE.INVITE_USER)"
        class="inline-block w-full cursor-pointer text-center"
        :class="[
          {
            'stroke-primary-default border-b-4 pb-2.5': collabTabStatus == COLLAB_TYPE.INVITE_USER
          }
        ]"
      >
        {{ COLLAB_TYPE.INVITE_USER }}
      </div>
      <div
        @click="handleCollabTabStatus(COLLAB_TYPE.INVITE_GROUP)"
        class="inline-block w-full cursor-pointer text-center"
        :class="[
          {
            'stroke-primary-default border-b-4 pb-2.5': collabTabStatus == COLLAB_TYPE.INVITE_GROUP
          }
        ]"
      >
        {{ COLLAB_TYPE.INVITE_GROUP }}
      </div>
    </div>
    <div class="mx-4 h-[1px] w-full bg-new-secondary-300 dark:bg-new-neutral-700" />
    <div class="flex w-full flex-col items-start gap-2 pb-3 pt-12 sm:flex-row">
      <div class="flex w-full flex-grow flex-col items-start gap-2">
        <span class="text-high text-sm font-semibold">{{
          collabTabStatus === COLLAB_TYPE.INVITE_USER ? 'Add user' : 'Add user groups'
        }}</span>
        <div
          class="background-primary stroke-input-default flex w-full flex-grow items-center gap-2 rounded-lg border"
          @click="toggleDropdown"
          ref="dropdownContainer"
        >
          <div class="relative flex w-full flex-wrap gap-2 p-2">
            <!-- Render Tags -->
            <div
              v-for="(tag, index) in tags"
              :key="index"
              class="stroke-secondary-default inline-flex w-fit items-center rounded-full border bg-white py-1 pl-3 pr-2 text-sm font-medium text-gray-800 shadow-sm"
            >
              {{ tag }}
              <button
                type="button"
                class="ml-2 rounded-full bg-new-neutral-200 p-1 text-gray-500 hover:text-gray-700 focus:outline-none"
                @click.stop="removeTag(index)"
              >
                <img :src="closeTag" alt="" class="h-2 w-2" />
              </button>
            </div>
            <input
              v-model="searchCollaborator"
              type="text"
              class="background-primary flex w-fit flex-grow border-none py-1 outline-none focus:ring-0"
              placeholder="Type a name"
            />
            <div
              v-if="showDropdown"
              class="background-primary stroke-input-default absolute left-0 z-10 w-full overflow-auto rounded-md border shadow-lg"
              :class="userList.length > 3 ? 'h-40 sm:h-28' : 'h-fit'"
              :style="{ top: `${dropdownOffset}px` }"
            >
              <span
                class="flex w-full items-center justify-center text-sm"
                :class="{ 'py-10': isGenericLoading.message }"
              >
                {{ isGenericLoading.message }}
              </span>
              <span
                v-if="searchUserList.length === 0 && !isGenericLoading.message"
                class="flex h-full min-h-12 w-full items-center justify-center"
              >
                Invite list is empty</span
              >
              <div
                v-for="(user, index) in searchUserList"
                :key="index"
                class="flex w-full cursor-pointer items-center justify-between p-2 hover:bg-gray-100 dark:hover:bg-gray-500"
                @click="addTag(user as User)"
              >
                <span>{{ user?.firstName ?? user?.name }} {{ user?.lastName }}</span>

                <img
                  v-if="tags.includes(user?.name as string) || tagsAPI.includes(user?.email as string )"
                  :src="tick"
                  alt=""
                  class="h-3 w-3"
                />
              </div>
            </div>
          </div>
        </div>
        <span
          class="text-sm font-medium"
          v-if="httpStatus?.status"
          :class="httpStatus.status === GENERIC_TYPE.ERROR ? 'error' : 'text-low'"
        >
          {{ httpStatus.message }}
        </span>
      </div>
      <div
        class="relative flex h-full w-full flex-col items-start justify-start gap-2 sm:w-1/3"
        ref="permissionDropdownContainer"
      >
        <span class="text-high text-sm font-semibold">Permission</span>
        <div
          class="color-secondary-600 stroke-input-default background-primary flex w-full cursor-pointer items-center justify-between gap-1 rounded-lg border px-3 py-3"
          @click="() => (permissionDropdown = !permissionDropdown)"
        >
          <p class="text-low text-base">
            {{ readOnlyPermission ? 'Viewer' : 'Editor' }}
          </p>
          <FontAwesomeIcon icon="fa-angle-down" class="h-3 w-3" />
        </div>
        <div
          v-if="permissionDropdown"
          class="stroke-input-default background-primary absolute -bottom-20 z-10 flex w-full flex-col rounded-md border p-1 text-new-secondary dark:text-white"
        >
          <span
            class="cursor-pointer rounded-md border border-transparent px-3 py-1 hover:border-[#9EB1D5]"
            :class="readOnlyPermission === false && 'bg-[#D0DCF0] text-new-secondary'"
            @click="changePermissionStatus(false)"
            >Editor</span
          >
          <span
            class="cursor-pointer rounded-md border border-transparent px-3 py-1 hover:border-[#9EB1D5]"
            :class="readOnlyPermission === true && 'bg-[#D0DCF0] text-new-secondary'"
            @click="changePermissionStatus(true)"
            >Viewer</span
          >
        </div>
      </div>
      <div class="w-full pt-2 sm:w-1/5 sm:pt-7">
        <Button
          variant="filled"
          size="small"
          color="secondary"
          class="w-full whitespace-nowrap px-2 py-3 text-sm md:w-32"
          :disabled="tags.length === 0 || httpStatus.status === GENERIC_TYPE.LOADING"
          @click="addCollaborator"
          >{{
            collabTabStatus === COLLAB_TYPE.INVITE_GROUP ? 'Invite groups' : 'Invite user'
          }}</Button
        >
      </div>
    </div>
    <div class="item-start flex w-full flex-col pt-8">
      <div class="flex gap-3">
        <input
          type="checkbox"
          class="rounded-sm accent-gray-700 dark:accent-gray-400"
          v-model="forkProjectCheck"
        /><span class="text-hight pl-0.5 text-base font-medium"
          >Fork and assign a project copy to the user.</span
        >
      </div>
      <span class="text-mid px-7 pt-3 text-sm font-normal"
        >Fork creates a copy of the project where you and the invited person can collaborate. Use
        this option if you want your users to work on their own version of the project.</span
      >
    </div>
  </div>
</template>
